import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';
import { History, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = { governance: '#0F2040', content_readiness: '#4ABFCF', integration_complexity: '#7C3AED', org_complexity: '#F59E0B',
  timeline_pressure: '#E11D48', client_capability: '#059669', change_readiness: '#0EA5E9', hosting: '#64748B', data_integrity: '#A16207' };
const RANGES = [{ days: 90, step: 7, label: '90d' }, { days: 180, step: 14, label: '6m' }, { days: 365, step: 14, label: '1y' }];
const fmtD = (v) => (v == null ? '—' : `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(2)}`);
const shortDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

/** Margin tab: how the portfolio average multiplier for each project factor has moved over time. */
export function PortfolioFactorHistoryCard() {
  const [range, setRange] = useState(RANGES[2]);
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(new Set());
  useEffect(() => {
    setData(null);
    axios.get(`${API_URL}/api/insights/margin/factor-history?days=${range.days}&step=${range.step}`, { withCredentials: true })
      .then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [range]);
  const keys = useMemo(() => (data?.factors || []).map((f) => f.key), [data]);
  const toggle = (k) => setHidden((h) => { const n = new Set(h); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const points = data?.points || [];
  const domain = useMemo(() => {
    const vals = points.flatMap((p) => keys.filter((k) => !hidden.has(k)).map((k) => p[k]).filter((v) => v != null));
    if (!vals.length) return [0.9, 1.1];
    return [Math.min(0.95, Math.min(...vals) - 0.02), Math.max(1.05, Math.max(...vals) + 0.02)];
  }, [points, keys, hidden]);
  return (
    <Card className="border-slate-200" data-testid="factor-history-card">
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <History className="w-4 h-4 text-[#0F2040]" />
          <h3 className="text-sm font-semibold text-[#0F2040]">Portfolio factor history</h3>
          <span className="text-xs text-slate-400">· {data?.open_deals_now ?? '—'} open deals now</span>
          <div className="ml-auto inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="factor-history-range">
            {RANGES.map((r) => (
              <button key={r.days} type="button" onClick={() => setRange(r)} data-testid={`factor-history-range-${r.label}`}
                className={`px-2 py-0.5 text-[11px] font-medium ${range.days === r.days ? 'bg-[#0F2040] text-white' : 'text-slate-600 hover:bg-slate-50'}`}>{r.label}</button>
            ))}
          </div>
        </div>
        <p className="text-xs text-slate-500 mb-3">Average multiplier per project factor across open deals at each checkpoint, rebuilt from version snapshots. ×1.00 is neutral; click a factor to hide or show it.</p>
        {!data && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        {data?.error && <p className="text-xs text-rose-600">Could not load factor history.</p>}
        {data && !data.error && (
          <>
            <div className="h-48" data-testid="factor-history-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={points} margin={{ top: 6, right: 12, left: -18, bottom: 0 }}>
                  <CartesianGrid stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 10, fill: '#94A3B8' }} minTickGap={28} />
                  <YAxis domain={domain} tickFormatter={(v) => `×${v.toFixed(2)}`} tick={{ fontSize: 10, fill: '#94A3B8' }} width={56} />
                  <ReferenceLine y={1} stroke="#CBD5E1" strokeDasharray="3 3" />
                  <Tooltip contentStyle={{ fontSize: 11 }} labelFormatter={(d, payload) => `${shortDate(d)} · ${payload?.[0]?.payload?.open_deals ?? '—'} open deals`}
                    formatter={(v, name) => [v == null ? '—' : `×${Number(v).toFixed(3)}`, data.factors.find((f) => f.key === name)?.label || name]} />
                  {keys.filter((k) => !hidden.has(k)).map((k) => (
                    <Line key={k} type="monotone" dataKey={k} stroke={COLORS[k] || '#0F2040'} strokeWidth={k === 'hosting' ? 1 : 1.8} dot={false} connectNulls isAnimationActive={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <ul className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1" data-testid="factor-history-legend">
              {data.factors.map((f) => (
                <li key={f.key}>
                  <button type="button" onClick={() => toggle(f.key)} className={`w-full flex items-center justify-between gap-2 text-[11px] py-0.5 ${hidden.has(f.key) ? 'opacity-40' : ''}`} data-testid={`factor-history-toggle-${f.key}`}>
                    <span className="inline-flex items-center gap-1.5 min-w-0"><span className="w-2.5 h-0.5 rounded shrink-0" style={{ background: COLORS[f.key] || '#0F2040' }} /><span className="truncate text-slate-700">{f.label}</span></span>
                    <span className="tabular-nums shrink-0 text-slate-500" data-testid={`factor-history-change-${f.key}`}>
                      {f.first == null ? '—' : <>×{f.first.toFixed(2)} → <b className="text-[#0F2040]">×{f.last.toFixed(2)}</b> <span className={f.change > 0.005 ? 'text-rose-600' : f.change < -0.005 ? 'text-emerald-600' : 'text-slate-400'}>{fmtD(f.change)}</span></>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}
